import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Spin,
  message,
  theme as antdTheme,
} from 'antd';
import {
  DeleteOutlined,
  IdcardOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';
import {
  certificateLabels,
  crewProfileGetMine,
  crewProfileSaveMine,
  type CrewCertificate,
  type CrewProfile,
  type ProductManagementRecord,
  type UpsertCrewProfilePayload,
} from '@/services/crewProfile';
import styles from './CrewProfile.module.css';

const certificateOptions = [
  { label: certificateLabels.fund, value: 'fund' },
  { label: certificateLabels.securities, value: 'securities' },
  { label: certificateLabels.cfa, value: 'cfa' },
  { label: certificateLabels.cpa, value: 'cpa' },
  { label: certificateLabels.frm, value: 'frm' },
  { label: certificateLabels.other, value: 'other' },
];

const formatDate = (value?: string | null) => (value ? dayjs(value) : undefined);

function CrewProfilePage() {
  const navigate = useNavigate();
  const userInfo = useUserStore((s) => s.userInfo);
  const [form] = Form.useForm();
  const [productForm] = Form.useForm<{ records: ProductManagementRecord[] }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productRecords, setProductRecords] = useState<ProductManagementRecord[]>([]);
  const selectedCertificates = Form.useWatch('certificates', form) as CrewCertificate[] | undefined;

  const displayName = userInfo?.realName || userInfo?.username || userInfo?.email || '未命名机组';
  const adenId = userInfo?.id || 'N/A';

  useEffect(() => {
    crewProfileGetMine()
      .then((data) => {
        applyProfileToForm(data.profile);
      })
      .catch(() => {
        applyProfileToForm(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const applyProfileToForm = (profile: CrewProfile | null) => {
    const records = profile?.productManagementRecords ?? [];
    setProductRecords(records);
    form.setFieldsValue({
      nationalId: profile?.nationalId ?? '',
      birthday: formatDate(profile?.birthday),
      birthdayCalendar: profile?.birthdayCalendar ?? 'solar',
      hireDate: formatDate(profile?.hireDate),
      phone: profile?.phone ?? userInfo?.phone ?? '',
      homeAddress: profile?.homeAddress ?? '',
      emergencyContact: profile?.emergencyContact ?? { name: '', phone: '' },
      lastMedicalCheckDate: formatDate(profile?.lastMedicalCheckDate),
      certificates: profile?.certificates ?? [],
      certificateOther: profile?.certificateOther ?? '',
    });
  };

  const openProductModal = () => {
    productForm.setFieldsValue({
      records: productRecords.length > 0
        ? productRecords
        : [{ year: new Date().getFullYear(), scale: '', returnRate: null }],
    });
    setProductModalOpen(true);
  };

  const saveProductRecords = async () => {
    const values = await productForm.validateFields();
    setProductRecords(values.records ?? []);
    setProductModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: UpsertCrewProfilePayload = {
        nationalId: values.nationalId,
        birthday: values.birthday.format('YYYY-MM-DD'),
        birthdayCalendar: values.birthdayCalendar,
        hireDate: values.hireDate ? values.hireDate.format('YYYY-MM-DD') : null,
        phone: values.phone,
        homeAddress: values.homeAddress,
        emergencyContact: values.emergencyContact,
        lastMedicalCheckDate: values.lastMedicalCheckDate
          ? values.lastMedicalCheckDate.format('YYYY-MM-DD')
          : null,
        certificates: values.certificates ?? [],
        certificateOther: values.certificateOther || null,
        productManagementRecords: productRecords,
      };

      setSaving(true);
      const saved = await crewProfileSaveMine(payload);
      applyProfileToForm(saved.profile);
      message.success('机组信息已保存');
    } catch {
      /* 表单校验和请求拦截器已处理提示 */
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.darkAlgorithm,
        token: {
          colorPrimary: '#00ffff',
          colorInfo: '#00ffff',
          colorBgContainer: 'rgba(10, 22, 44, 0.86)',
          colorBgElevated: 'rgba(10, 22, 44, 0.96)',
          colorBorder: 'rgba(0, 255, 255, 0.18)',
          colorBorderSecondary: 'rgba(0, 255, 255, 0.12)',
          fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif",
          borderRadius: 4,
        },
      }}
    >
      <div className={styles.page}>
        <div className={styles.starfield} />
        <div className={styles.gridBackground} />
        <span className={`${styles.hudCorner} ${styles.tl}`} />
        <span className={`${styles.hudCorner} ${styles.tr}`} />
        <span className={`${styles.hudCorner} ${styles.bl}`} />
        <span className={`${styles.hudCorner} ${styles.br}`} />

        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <span className={styles.crumb}>AdenCockpit / CREW PROFILE</span>
              <span className={styles.title}>
                <IdcardOutlined className={styles.titleIcon} />
                机组信息管理
              </span>
              <span className={styles.subtitle}>CREW · IDENTITY · QUALIFICATION</span>
            </div>
            <Button
              icon={<RollbackOutlined />}
              className={styles.backBtn}
              onClick={() => navigate('/dashboard')}
            >
              返回驾驶舱
            </Button>
          </div>

          <Spin spinning={loading}>
            <div className={styles.panel}>
              <div className={styles.identityGrid}>
                <div className={styles.identityItem}>
                  <div className={styles.identityLabel}>姓名</div>
                  <div className={styles.identityValue}>{displayName}</div>
                </div>
                <div className={styles.identityItem}>
                  <div className={styles.identityLabel}>AdenID</div>
                  <div className={styles.identityValue}>{adenId}</div>
                </div>
              </div>

              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <div className={styles.sectionTitle}>基础信息</div>
                <div className={styles.formGrid}>
                  <Form.Item
                    name="nationalId"
                    label="身份证号码"
                    rules={[{ required: true, message: '请输入身份证号码' }]}
                  >
                    <Input placeholder="请输入身份证号码" />
                  </Form.Item>

                  <Form.Item label="生日" required>
                    <div className={styles.dateWithMode}>
                      <Form.Item
                        name="birthday"
                        noStyle
                        rules={[{ required: true, message: '请选择生日' }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="birthdayCalendar" noStyle>
                        <Radio.Group
                          optionType="button"
                          buttonStyle="solid"
                          options={[
                            { label: '公历', value: 'solar' },
                            { label: '农历', value: 'lunar' },
                          ]}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>

                  <Form.Item name="hireDate" label="入职时间">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="联系电话"
                    rules={[{ required: true, message: '请输入联系电话' }]}
                  >
                    <Input placeholder="请输入联系电话" />
                  </Form.Item>

                  <Form.Item
                    name="homeAddress"
                    label="家庭住址"
                    className={styles.fullWidth}
                    rules={[{ required: true, message: '请输入家庭住址' }]}
                  >
                    <Input.TextArea rows={3} placeholder="请输入家庭住址" />
                  </Form.Item>
                </div>

                <div className={styles.sectionTitle}>紧急联系人</div>
                <div className={styles.formGrid}>
                  <Form.Item
                    name={['emergencyContact', 'name']}
                    label="姓名"
                    rules={[{ required: true, message: '请输入紧急联系人姓名' }]}
                  >
                    <Input placeholder="请输入紧急联系人姓名" />
                  </Form.Item>
                  <Form.Item
                    name={['emergencyContact', 'phone']}
                    label="电话"
                    rules={[{ required: true, message: '请输入紧急联系人电话' }]}
                  >
                    <Input placeholder="请输入紧急联系人电话" />
                  </Form.Item>
                </div>

                <div className={styles.sectionTitle}>资质与健康</div>
                <div className={styles.formGrid}>
                  <Form.Item name="lastMedicalCheckDate" label="上次体检时间">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name="certificates"
                    label="资质证书信息"
                    className={styles.fullWidth}
                  >
                    <Checkbox.Group options={certificateOptions} />
                  </Form.Item>

                  {selectedCertificates?.includes('other') && (
                    <Form.Item
                      name="certificateOther"
                      label="其他证书"
                      className={styles.fullWidth}
                      rules={[{ required: true, message: '请填写其他证书' }]}
                    >
                      <Input placeholder="请输入其他证书名称" />
                    </Form.Item>
                  )}
                </div>

                <div className={styles.sectionTitle}>产品管理情况</div>
                <div className={styles.productSummary}>
                  <div className={styles.productCount}>
                    已维护 {productRecords.length} 条产品管理记录
                  </div>
                  <Button
                    icon={<TeamOutlined />}
                    className={styles.ghostBtn}
                    onClick={openProductModal}
                  >
                    维护产品管理情况
                  </Button>
                </div>

                <div className={styles.actionBar}>
                  <Button className={styles.ghostBtn} onClick={() => navigate('/dashboard')}>
                    取消
                  </Button>
                  <Button
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={saving}
                    className={styles.primaryBtn}
                  >
                    保存信息
                  </Button>
                </div>
              </Form>
            </div>
          </Spin>
        </div>

        <Modal
          title="产品管理情况"
          open={productModalOpen}
          onOk={saveProductRecords}
          onCancel={() => setProductModalOpen(false)}
          okText="保存记录"
          cancelText="取消"
          width={760}
        >
          <Form form={productForm} layout="vertical">
            <Form.List name="records">
              {(fields, { add, remove }) => (
                <>
                  <div className={styles.productRows}>
                    {fields.map((field) => (
                      <div key={field.key} className={styles.productRow}>
                        <Form.Item
                          name={[field.name, 'year']}
                          label="年份"
                          rules={[{ required: true, message: '请输入年份' }]}
                        >
                          <InputNumber min={2000} max={2100} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'scale']}
                          label="规模"
                          rules={[{ required: true, message: '请输入规模' }]}
                        >
                          <Input placeholder="例如 3.5 亿" />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'returnRate']}
                          label="收益率"
                          rules={[{ required: true, message: '请输入收益率' }]}
                        >
                          <InputNumber addonAfter="%" style={{ width: '100%' }} />
                        </Form.Item>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          style={{ marginTop: 30 }}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    ))}
                  </div>
                  <div className={styles.productActions}>
                    <Button icon={<PlusOutlined />} onClick={() => add({
                      year: new Date().getFullYear(),
                      scale: '',
                      returnRate: null,
                    })}>
                      增加记录
                    </Button>
                    <Button onClick={() => productForm.setFieldsValue({ records: [] })}>
                      清空记录
                    </Button>
                  </div>
                </>
              )}
            </Form.List>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
}

export default CrewProfilePage;
